import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  FormField,
  Heading,
  Input,
  Row,
  Textarea,
  Text,
  Spinner,
  useToast,
} from "@/components/ds";
import { GPS_RADIUS_DEFAULT } from "@/domain/sites/types";
import { ChevronRightIcon } from "@/icons/ChevronRightIcon";
import { SiteMap } from "./SiteMapLazy";
import { RadiusPicker } from "./RadiusPicker";
import { useGeocode } from "./useGeocode";
import styles from "./SiteForm.module.css";

export function SiteForm() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [gpsRadiusMetres, setGpsRadiusMetres] = useState(String(GPS_RADIUS_DEFAULT));
  const [manualLat, setManualLat] = useState<number | null>(null);
  const [manualLng, setManualLng] = useState<number | null>(null);
  const [clientContactName, setClientContactName] = useState("");
  const [clientContactEmail, setClientContactEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notFoundDismissed, setNotFoundDismissed] = useState(false);

  const [pinPlaced, setPinPlaced] = useState(false);
  const {
    location: geocodedLocation,
    pendingLocation,
    loading: geocoding,
    notFound,
    acceptPending,
    dismissPending,
  } = useGeocode(postcode, pinPlaced);

  const mapLat = manualLat ?? geocodedLocation?.latitude ?? null;
  const mapLng = manualLng ?? geocodedLocation?.longitude ?? null;

  useEffect(() => {
    if (mapLat != null && !pinPlaced) {
      setPinPlaced(true);
    }
  }, [mapLat, pinPlaced]);

  useEffect(() => {
    if (notFound) {
      setNotFoundDismissed(false);
    }
  }, [notFound]);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setManualLat(lat);
    setManualLng(lng);
    setNotFoundDismissed(true);
  }, []);

  const handleAcceptPending = useCallback(() => {
    acceptPending();
    setManualLat(null);
    setManualLng(null);
  }, [acceptPending]);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setErrors({});

    try {
      const response = await fetch("/api/sites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          address: address || null,
          postcode: postcode || null,
          latitude: mapLat,
          longitude: mapLng,
          gpsRadiusMetres: Number(gpsRadiusMetres),
          clientContactName: clientContactName || null,
          clientContactEmail: clientContactEmail || null,
          notes: notes || null,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        if (data.error?.fields) {
          setErrors(data.error.fields);
        } else {
          toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        }
        return;
      }

      toast("success", "Site added");
      navigate("/sites");
    } finally {
      setSaving(false);
    }
  }, [name, address, postcode, mapLat, mapLng, gpsRadiusMetres, clientContactName, clientContactEmail, notes, navigate, toast]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Heading level={1} size="h3">Add a new site</Heading>
        <Text size="sm" color="muted">
          Where does your team clean? Add the address and we&apos;ll set up GPS check-in.
        </Text>
      </div>

      <div className={styles.form}>
        <div className={styles.fields}>
          <FormField label="Site name" htmlFor="name" error={errors.name} required>
            <Input
              id="name"
              name="name"
              placeholder="e.g. Acme Corp HQ"
              value={name}
              onChange={(e) => { setName(e.target.value); clearError("name"); }}
              error={!!errors.name}
            />
          </FormField>

          <FormField label="Address" htmlFor="address" error={errors.address}>
            <Input
              id="address"
              name="address"
              placeholder="Full street address"
              value={address}
              onChange={(e) => { setAddress(e.target.value); clearError("address"); }}
              error={!!errors.address}
            />
          </FormField>

          <FormField label="Postcode" htmlFor="postcode" error={errors.postcode}>
            <Input
              id="postcode"
              name="postcode"
              placeholder="e.g. SW1A 1AA"
              value={postcode}
              onChange={(e) => { setPostcode(e.target.value); clearError("postcode"); setManualLat(null); setManualLng(null); }}
              error={!!errors.postcode}
            />
          </FormField>

          <button
            type="button"
            className={styles.disclosureButton}
            onClick={() => setDetailsOpen(!detailsOpen)}
            aria-expanded={detailsOpen}
          >
            <ChevronRightIcon
              width={16}
              height={16}
              className={`${styles.disclosureChevron} ${detailsOpen ? styles.disclosureOpen : ""}`}
            />
            <span>Contact and notes</span>
          </button>

          {detailsOpen && (
            <div className={styles.detailsSection}>
              <FormField label="Contact name" htmlFor="clientContactName">
                <Input
                  id="clientContactName"
                  name="clientContactName"
                  placeholder="e.g. John Smith"
                  value={clientContactName}
                  onChange={(e) => setClientContactName(e.target.value)}
                />
              </FormField>

              <FormField label="Contact email" htmlFor="clientContactEmail" error={errors.clientContactEmail}>
                <Input
                  id="clientContactEmail"
                  name="clientContactEmail"
                  type="email"
                  placeholder="e.g. john@acme.com"
                  value={clientContactEmail}
                  onChange={(e) => { setClientContactEmail(e.target.value); clearError("clientContactEmail"); }}
                  error={!!errors.clientContactEmail}
                />
              </FormField>

              <FormField label="Notes" htmlFor="notes" hint="Visible to cleaners on their shift view">
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="e.g. Use the side entrance, code 4521"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </FormField>
            </div>
          )}

          <Row gap={3} className={styles.actions}>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? "Adding..." : "Add site"}
            </Button>
            <Button variant="ghost" href="/sites">
              Cancel
            </Button>
          </Row>
        </div>

        <div className={styles.mapPanel}>
          <div className={styles.mapContainer}>
            {notFound && !geocoding && !notFoundDismissed && (
              <div className={styles.locationPrompt}>
                <Text size="sm">We couldn&apos;t find that postcode. Click the map to place a pin.</Text>
                <div className={styles.promptButtons}>
                  <Button size="sm" variant="ghost" onClick={() => setNotFoundDismissed(true)}>Dismiss</Button>
                </div>
              </div>
            )}
            {pendingLocation && (
              <div className={styles.locationPrompt}>
                <Text size="sm">New location found. Use it?</Text>
                <div className={styles.promptButtons}>
                  <Button size="sm" onClick={handleAcceptPending}>Update</Button>
                  <Button size="sm" variant="ghost" onClick={dismissPending}>Keep current</Button>
                </div>
              </div>
            )}
            <SiteMap
              latitude={mapLat}
              longitude={mapLng}
              radiusMetres={Number(gpsRadiusMetres)}
              onLocationChange={handleMapClick}
            />
            {geocoding && (
              <div className={styles.mapLoading}>
                <Spinner size="sm" />
              </div>
            )}
          </div>
          <RadiusPicker
            value={gpsRadiusMetres}
            onChange={setGpsRadiusMetres}
          />
        </div>
      </div>
    </div>
  );
}
